import React, { useState, useEffect, useRef } from 'react';
import { useThrottleFn, useMemoizedFn } from 'ahooks';
import { Anchor } from 'antd';
import _isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { useMonthlyStore } from '@/components/EcocreditsMonthlyMadness/MonthlyProvider';

import { getIsPadWidth } from '@elem/utils/devices';
import EcoAnchor from '../EcoAnchor';
import { useStore } from '../context';
import styled from './index.module.scss';
import { EU11TimeLineProd, eu11LocaleList } from '../config';
import { useGlobal } from '@/utils/useGlobal';

const { Link } = Anchor;

const EUEcoAnchorNew = () => {
    const [isVisible, setIsVisible] = useState(false);
    const compRef = useRef<HTMLDivElement | null>(null);
    const navContainerRef: any = useRef(null);
    const anchorRef: any = useRef(null);

    const { locale }: any = useRouter();
    const isMobile = getIsPadWidth();
    const { isLogin } = useMonthlyStore();

    const { useLang, getSystemTime } = useGlobal();
    const time = getSystemTime();
    const { t } = useLang('ecocredits.new_nav');
    const [tabPosition, setTabPosition] = useState({
        tabWidth: 0,
        offsetLeft: 0,
    });
    const [lockTab, setLockTab] = useState(false); // 当点击时不触发滚动事件
    const [anchorClass, setAnchorClass] = useState('');
    const timeLine = EU11TimeLineProd[locale as keyof typeof EU11TimeLineProd] || EU11TimeLineProd['eu'];
    // 活动的临时导航
    const [navArr, setNavArr] = useState<
        | {
              href: string;
              title: string;
          }[]
        | null
    >(null);

    const allNavData = [
        {
            href: '#Check_In',
            title: t('nav_1'),
        },
        {
            href: '#FLASH_SALE',
            title: t('nav_2'),
        },
        {
            href: '#BEST_OFFERS',
            title: t('nav_3'),
        },
        {
            href: '#HOT_PICK',
            title: t('nav_4'),
        },
        {
            href: '#HOW_IT_WORKS',
            title: t('nav_5'),
        },
        {
            href: '#ECO_BENEFITS',
            title: t('nav_6'),
        },
        {
            href: '#WAYS_TO_EARN_POINTS',
            title: t('nav_7'),
        },
        {
            href: '#ECOCREDIT_RULES',
            title: t('nav_8'),
        },
        {
            href: '#ECO_FAQ',
            title: t('nav_9'),
        },
    ];

    const checkActivityNav = () => {
        if (eu11LocaleList.includes(locale)) {
            const arr = allNavData.filter((item, index) => {
                if (index === 0 && timeLine.CheckIn.start <= time && time <= timeLine.CheckIn.end) return true;

                if (index === 1 && timeLine.FlashSale.start <= time && time <= timeLine.FlashSale.end) return true;

                if (index === 2 && timeLine.BestOffers.start <= time && time <= timeLine.BestOffers.end) return true;

                if (index === 3 && timeLine.HotPicks.start <= time && time <= timeLine.HotPicks.end) return true;

                if (index === 4 && !isLogin) return true;

                if (index === 5 || index === 6 || index === 7 || index === 8) return true;

                return false;
            });

            setNavArr(arr);
        }
    };

    const onActiveChange = (currentActiveLink: string) => {
        if (lockTab) return;
        if (!currentActiveLink) {
            setTabPosition({
                ...tabPosition,
                tabWidth: 0,
            });
            return;
        }
        const currLink = document.querySelector<HTMLLinkElement>(`.ant-anchor-link-title-active[href="${currentActiveLink}"]`);
        if (!currLink) return;
        const { clientWidth, offsetLeft } = currLink;
        setTabPosition({
            tabWidth: clientWidth,
            offsetLeft,
        });
        // if (!isMobile) return;
        scrollBar(offsetLeft);
    };

    const handleClick = (_e: any, current: any) => {
        if (!current.href) {
            setTabPosition({
                ...tabPosition,
                tabWidth: 0,
            });
            return;
        }
        const currLink = document.querySelector<HTMLLinkElement>(`.ant-anchor-link-title[href="${current.href}"]`);
        if (!currLink) return;
        const { clientWidth, offsetLeft } = currLink;
        setTabPosition({
            tabWidth: clientWidth,
            offsetLeft,
        });
        // 点击后禁止触发onChange事件
        setLockTab(true);
        setTimeout(() => {
            setLockTab(false);
        }, 1000);
        // if (!isMobile) return;
        scrollBar(offsetLeft);
    };

    const { run: onActiveChangeRun } = useThrottleFn(onActiveChange, {
        wait: 100,
        leading: false,
    });

    const scrollBar = useMemoizedFn((offsetLeft: number) => {
        const wrapper = document.querySelector<HTMLDivElement>('.ant-anchor-wrapper');
        if (!wrapper) return;

        wrapper.scrollTo({
            left: offsetLeft - 20,
            behavior: 'smooth',
        });
    });

    // 调整导航栏的位置
    const adjustNavPosition = () => {
        if (!navContainerRef.current) return;

        const anchorElement: any = document.querySelector('.ant-anchor-wrapper');
        if (!anchorElement) return;

        const anchorWidth = anchorElement.offsetWidth;

        if (anchorWidth > window.innerWidth) {
            setAnchorClass('eco_anchor_new_left');
        } else {
            setAnchorClass('');
        }
    };

    useEffect(() => {
        const firstLink = document.querySelector<HTMLLinkElement>(`.ant-anchor-link-title[href="#WAYS_TO_EARN_POINTS"]`);
        checkActivityNav();
        if (!firstLink) return;
        const { offsetLeft } = firstLink;
        setTabPosition({
            tabWidth: 0,
            offsetLeft,
        });

        const calcDistance = () => {
            if (compRef.current) {
                const rect = compRef.current.getBoundingClientRect();
                setIsVisible(rect.top <= 0);
            }
        };

        const debounceFunc = debounce(calcDistance, 0);

        window.addEventListener('scroll', debounceFunc);
        window.addEventListener('resize', debounceFunc);

        return () => {
            window.removeEventListener('scroll', debounceFunc);
            window.removeEventListener('resize', debounceFunc);
        };
    }, []);

    useEffect(() => {
        adjustNavPosition();
        window.addEventListener('resize', adjustNavPosition);

        return () => {
            window.addEventListener('resize', adjustNavPosition);
        };
    }, []);

    return (
        <div ref={compRef} className={styled.eu_eco_nav_new}>
            <div className={`eco_anchor_new ${anchorClass}`} ref={navContainerRef}>
                <Anchor targetOffset={50} showInkInFixed={false} onClick={handleClick} onChange={onActiveChangeRun} ref={anchorRef}>
                    {navArr?.map((item: any) => (
                        <Link href={item.href} title={item.title} key={item.title} />
                    ))}

                    <span
                        className="active-line"
                        style={{
                            width: `calc(${tabPosition.tabWidth}px)`,
                            left: `calc(${tabPosition.offsetLeft}px)`,
                        }}
                    />
                </Anchor>
            </div>
        </div>
    );
};

export default EUEcoAnchorNew;
